import { create } from 'zustand';
import { platoonApi } from '../services/api';
import type { Platoon, PlatoonPhase } from '../types/domain';

interface PlatoonState {
  platoons: Platoon[];
  isLoading: boolean;
  error: string | null;

  fetchPlatoons: (params?: { unitId?: string; farmbaseId?: string; phase?: string }) => Promise<void>;
  getPlatoonsForFarmbase: (farmbaseId: string) => Platoon[];
  getPlatoonsForUnit: (unitId: string) => Platoon[];
  getPlatoonById: (id: string) => Platoon | undefined;

  // Reconcile a platoon from a socket echo / API response (immutable upsert).
  upsertPlatoon: (platoon: Platoon) => void;
  applyPhase: (platoonId: string, phase: PlatoonPhase, operationId?: string | null) => void;

  // Optimistic phase set: update locally, POST, reconcile via platoon:phase echo; rollback on error.
  setPhase: (
    platoonId: string,
    phase: PlatoonPhase,
    coords?: { lat?: number; lng?: number },
    note?: string
  ) => Promise<void>;
}

export const usePlatoonStore = create<PlatoonState>((set, get) => ({
  platoons: [],
  isLoading: false,
  error: null,

  fetchPlatoons: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await platoonApi.getAll(params);
      set({ platoons: response.data.platoons || [], isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to fetch platoons',
        isLoading: false,
      });
    }
  },

  getPlatoonsForFarmbase: (farmbaseId) => get().platoons.filter((p) => p.farmbaseId === farmbaseId),
  getPlatoonsForUnit: (unitId) => get().platoons.filter((p) => p.unitId === unitId),
  getPlatoonById: (id) => get().platoons.find((p) => p.id === id),

  upsertPlatoon: (platoon) =>
    set((state) => {
      const exists = state.platoons.some((p) => p.id === platoon.id);
      return {
        platoons: exists
          ? state.platoons.map((p) => (p.id === platoon.id ? { ...p, ...platoon } : p))
          : [...state.platoons, platoon],
      };
    }),

  applyPhase: (platoonId, phase, operationId) =>
    set((state) => ({
      platoons: state.platoons.map((p) =>
        p.id === platoonId
          ? { ...p, currentPhase: phase, currentOperationId: operationId ?? p.currentOperationId }
          : p
      ),
    })),

  setPhase: async (platoonId, phase, coords, note) => {
    const prev = get().platoons.find((p) => p.id === platoonId)?.currentPhase;
    // Optimistic update.
    get().applyPhase(platoonId, phase);
    try {
      await platoonApi.setPhase(platoonId, {
        phase,
        lat: coords?.lat,
        lng: coords?.lng,
        note,
      });
      // The platoon:phase socket echo reconciles the authoritative state.
    } catch (error) {
      // Roll back the optimistic change.
      if (prev) get().applyPhase(platoonId, prev);
      throw error;
    }
  },
}));
